// Notification slice — global notification state persisted in Zustand.
//
// Notifications flow: caller → pushNotification() → store update →
//   EventBus 'notification:new' → Toast bridge (toast popup) +
//   NotificationCenter (bell badge + dropdown list).
//
// Callers use the `useNotify()` hook (lib/hooks/use-notify.ts) which
// wraps pushNotification + EventBus emit in one call.
//
// Max 50 notifications stored; oldest non-persistent entries dropped.
//
// @extensible — Account system extension point: when the remote account
//   system lands, set `_remoteService` to an INotificationService
//   implementation. syncFromRemote() will pull server-side notifications
//   and merge them into the local store.

import type { StateCreator } from 'zustand';
import type {
  AppNotification,
  CreateNotification,
  NotificationLevel,
  INotificationService,
} from '@examforge/shared';

export type { AppNotification, CreateNotification, NotificationLevel };

export interface NotificationSlice {
  // ── State ──────────────────────────────────────────────────────────────
  notifications: AppNotification[];
  unreadCount: number;

  // ── Actions ────────────────────────────────────────────────────────────
  /** Add a notification to the store. Returns the created notification's id. */
  pushNotification: (n: CreateNotification) => string;
  /** Mark a single notification as read. */
  markRead: (id: string) => void;
  /** Mark all notifications as read. */
  markAllRead: () => void;
  /** Remove a notification from the list. */
  dismiss: (id: string) => void;
  /** Remove all notifications. */
  clearAll: () => void;

  // ── Account extension point ───────────────────────────────────────────
  /**
   * Future: when set to a remote INotificationService, syncFromRemote()
   * pulls server-side notifications and merges them into the local store.
   * Set once at app bootstrap; never changes during the session.
   *
   * @extensible — swap this for RemoteNotificationService when accounts go live.
   */
  _remoteService: INotificationService | null;
  /** Pull notifications from the remote service (no-op when _remoteService is null). */
  syncFromRemote: () => Promise<void>;
  /** Expose the remote service's userId (null for anonymous). */
  _userId: string | null;
  /** 19BN Phase 5: inject a remote notification service (called on login). */
  setRemoteService: (service: INotificationService) => void;
}

const MAX_NOTIFICATIONS = 50;

let _notifSeq = 0;

const initialNotificationState = {
  notifications: [] as AppNotification[],
  unreadCount: 0,
  _remoteService: null as INotificationService | null,
  _userId: null as string | null,
};

export const createNotificationSlice: StateCreator<NotificationSlice> = (set, get) => ({
  ...initialNotificationState,

  pushNotification: (input: CreateNotification): string => {
    const id = `n${Date.now().toString(36)}_${(_notifSeq++).toString(36)}`;
    const notification: AppNotification = {
      ...input,
      id,
      timestamp: Date.now(),
      read: false,
    };
    set((s) => {
      const next = [...s.notifications, notification];
      // Cap at MAX_NOTIFICATIONS — drop oldest non-persistent first
      while (next.length > MAX_NOTIFICATIONS) {
        const dropIdx = next.findIndex((e) => !e.persistent);
        if (dropIdx >= 0) next.splice(dropIdx, 1);
        else next.shift();
      }
      return {
        notifications: next,
        unreadCount: next.filter((e) => !e.read).length,
      } as Partial<NotificationSlice>;
    });
    return id;
  },

  markRead: (id: string) => {
    set((s) => {
      const notifications = s.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      );
      return {
        notifications,
        unreadCount: notifications.filter((n) => !n.read).length,
      } as Partial<NotificationSlice>;
    });
  },

  markAllRead: () => {
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }) as Partial<NotificationSlice>);
  },

  dismiss: (id: string) => {
    set((s) => {
      const notifications = s.notifications.filter((n) => n.id !== id);
      return {
        notifications,
        unreadCount: notifications.filter((n) => !n.read).length,
      } as Partial<NotificationSlice>;
    });
  },

  clearAll: () => {
    set({
      notifications: [],
      unreadCount: 0,
    } as Partial<NotificationSlice>);
  },

  // 19BN Phase 5: inject remote service (called by use-auth-init on login).
  setRemoteService: (service) => {
    set({ _remoteService: service } as Partial<NotificationSlice>);
  },

  // ── Account extension point (no-op until _remoteService is set) ──────
  syncFromRemote: async () => {
    const { _remoteService } = get();
    if (!_remoteService) return;
    try {
      const remote = await _remoteService.fetchAll();
      if (remote.length === 0) return;
      set((s) => {
        // Merge remote notifications — dedupe by id, sort by timestamp desc
        const existingIds = new Set(s.notifications.map((n) => n.id));
        const fresh = remote.filter((n) => !existingIds.has(n.id));
        if (fresh.length === 0) return {} as Partial<NotificationSlice>;
        const merged = [...fresh, ...s.notifications]
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, MAX_NOTIFICATIONS);
        return {
          notifications: merged,
          unreadCount: merged.filter((n) => !n.read).length,
        } as Partial<NotificationSlice>;
      });
    } catch {
      // Silently ignore fetch failures — notifications are non-critical.
      // The UI already shows whatever is in the local store.
    }
  },
});
